// mixins are REUSABLE functions
// the code here will be inserted into a selected component during the created() lifecycle
// for this reason, the question variable here WILL be recognised by ANY component that uses the question prop
// all validation rules MUST be defined here
// each validation rule MUST return either a Boolean or a String
import {
  dateInRange,
  policyStartDateInRange,
  ruleDateOfClaimInRange,
} from "@/common/dates";
import moment from "moment";

export default {
  data() {
    return {
      rules: {
        ruleMaxLength: (value) => {
          const { Value, ErrorMsg } =
            this.question.Validation.RuleProperties.MaxLength;

          if (!value) {
            return true;
          } else {
            return (
              value.length <= Value ||
              `${this.question.ValidatedText ?? this.question.Text} ${ErrorMsg}`
            );
          }
        },
        ruleCurrencyMaxLength: (value) => {
          const { Value, ErrorMsg } =
            this.question.Validation.RuleProperties.CurrencyMaxLength;

          if (!value) {
            return true;
          } else {
            return (
              value.replaceAll(",", "").length <= Value ||
              `${this.question.ValidatedText ?? this.question.Text} ${ErrorMsg}`
            );
          }
        },
        ruleMinLength: (value) => {
          const { Value, ErrorMsg } =
            this.question.Validation.RuleProperties.MinLength;

          if (!value) {
            return true;
          } else {
            return (
              value.length >= Value ||
              `${this.question.ValidatedText ?? this.question.Text} ${ErrorMsg}`
            );
          }
        },
        ruleRegexFormat: (value) => {
          const { Value, ErrorMsg, Flags } =
            this.question.Validation.RuleProperties.RegexFormat;
          const pattern = new RegExp(Value, Flags != null ? Flags : "gm");

          if (!value) {
            return true;
          } else {
            return pattern.test(value) || `${ErrorMsg}`;
          }
        },
        rulePhoneNumberFormat: (value) => {
          if (!value) {
            return true;
          }

          const numberOnlyPattern = new RegExp("^\\d*$");
          if (!numberOnlyPattern.test(value)) {
            return `Please enter numerical values only.`;
          }

          const startingNumberPattern = new RegExp("^0[1237]");
          if (!startingNumberPattern.test(value)) {
            return `${this.question.Text} must begin with 01, 02, 03 or 07.`;
          }

          const length = value.length;
          if (length < 9) {
            return `${this.question.Text} must be at least 9 characters.`;
          }

          if (length > 15) {
            return `${this.question.Text} may not be greater than 15 characters.`;
          }

          return true;
        },
        ruleDateValid: (value) => {
          const { ErrorMsg } =
            this.question.Validation.RuleProperties.DateValid;
          const userValue = moment(value, "DD/MM/YYYY");

          if (!value) {
            return true;
          } else {
            if (moment(userValue).isValid()) {
              return true;
            } else {
              return `${ErrorMsg}`;
            }
          }
        },
        ruleDateOfClaim: (value) => {
          return ruleDateOfClaimInRange(
            value,
            this.question.Validation.RuleProperties.DateOfClaim,
            "years"
          );
        },
        ruleRequired: (value) => {
          return (
            (!!value &&
              (typeof value === "string" ? value.trim() !== "" : true)) ||
            this.question.Validation.RuleProperties?.Required?.ErrorMsg ||
            `${this.question.ValidatedText ?? this.question.Text} is required.`
          );
        },
        ruleAgeInRange: (value) => {
          const { MinValue, MaxValue, ErrorMsg } =
            this.question.Validation.RuleProperties.AgeInRange;
          const userValue = moment(value, "DD/MM/YYYY");
          const maxRange = moment().subtract(MinValue, "years"); // 18 - 2004
          const minRange = moment().subtract(MaxValue, "years"); // 105 - 1917
          const isInRange = moment(userValue).isBetween(
            moment(minRange),
            moment(maxRange),
            "days",
            "[]"
          );

          if (!value) {
            return true;
          } else {
            if (isInRange) {
              return true;
            } else {
              return `${ErrorMsg}`;
            }
          }
        },
        rulePolicyStartDate: (value) => {
          return policyStartDateInRange(
            value,
            this.question.Validation.RuleProperties.PolicyStartDate,
            "days"
          );
        },
        ruleMortgageStartDate: (value) => {
          return dateInRange(
            value,
            this.question.Validation.RuleProperties.MortgageStartDate,
            "years"
          );
        },
        rulePropertyYearInRange: (value) => {
          const { MinValue, ErrorMsg } =
            this.question.Validation.RuleProperties.PropertyYearInRange;
          const maxValue = new Date().getFullYear() + 1;
          if (!value) return true;
          return value < MinValue || value > maxValue ? ErrorMsg : true;
        },
        ruleValueInRange: (value) => {
          if (!value) {
            return true;
          }
          const { MinValue, MaxValue, ErrorMsg } =
            this.question.Validation.RuleProperties.ValueInRange;
          if (isNaN(value)) {
            return `${ErrorMsg}`;
          }

          const parsed = parseInt(value);
          if (parsed >= MinValue && parsed <= MaxValue) {
            return true;
          }
          return `${ErrorMsg}`;
        },
        ruleMinValue: (value) => {
          if (!value) {
            return true;
          }
          const { Value, ErrorMsg } =
            this.question.Validation.RuleProperties.MinValue;
          if (isNaN(value)) {
            return `${ErrorMsg}`;
          }

          const parsed = parseInt(value);
          if (parsed >= Value) {
            return true;
          }
          return `${ErrorMsg}`;
        },
        ruleMaxValue: (value) => {
          if (!value) {
            return true;
          }

          const { Value, ErrorMsg } =
            this.question.Validation.RuleProperties.MaxValue;
          if (isNaN(value)) {
            return `${ErrorMsg}`;
          }
          const parsed = parseInt(value);
          if (parsed <= Value) {
            return true;
          }
          return `${ErrorMsg}`;
        },
        ruleDateInYearValid: (value) => {
          const { Month, Year, ErrorMsg } =
            this.question.Validation.RuleProperties.DateInYearValid;
          if (!value || !Month || !Year) {
            return true;
          }
          const userValue = moment(
            `${value}/${Month}/${Year}`,
            "DD/MM/YYYY",
            false
          );

          if (moment(userValue).isValid()) {
            return true;
          } else {
            return `${ErrorMsg}`;
          }
        },
      },
    };
  },
  computed: {
    getRules() {
      if (!this.question) return;
      if (!this.question.Validation.Rules) {
        return [true];
      }
      return this.question.Validation.Rules.map((rule) => {
        // if the rules object has a key matching the item variable
        // return the function assigned to that key else return true
        // setting true means that the validation will always pass
        return this.rules[rule] || true;
      });
    },
  },
};
